import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Lock, Check, X, Users, Minus } from "lucide-react";
import { prefersReducedMotion } from "@/lib/scrollEngine";

/**
 * Hero side-by-side comparison: off-the-shelf SaaS (locked modules, per-seat
 * pricing, broken flow) next to custom software (only used modules, no seat
 * fees, flow end to end). Labels come from i18n (hero.visual.*).
 */
const Flow = ({ ok }: { ok: boolean }) => (
  <svg viewBox="0 0 120 30" className="w-full h-auto overflow-visible">
    <line x1="12" y1="15" x2="60" y2="15" className={ok ? "stroke-accent" : "stroke-foreground/20"} strokeWidth="1.5" strokeDasharray={ok ? "0" : "3 4"} />
    <line x1="60" y1="15" x2="108" y2="15" className={ok ? "stroke-accent" : "stroke-foreground/25"} strokeWidth="1.5" />
    {ok && <line x1="12" y1="15" x2="108" y2="15" className="stroke-accent hero-flow" strokeWidth="2.5" strokeLinecap="round" />}
    {[12, 60, 108].map((x, i) => (
      <g key={i}>
        <rect x={x - 8} y="7" width="16" height="16" rx="4" className={`fill-background ${ok ? "stroke-accent" : "stroke-foreground/30"}`} strokeWidth="1.2" />
        <rect x={x - 3} y="12" width="6" height="6" rx="1.5" className={ok ? "fill-accent hero-node" : "fill-foreground/30"} style={{ animationDelay: `${i * 0.5}s` }} />
      </g>
    ))}
    {!ok && (
      <g className="hero-node">
        <circle cx="36" cy="15" r="7" className="fill-destructive/25" />
        <path d="M33 12 L39 18 M39 12 L33 18" className="stroke-destructive" strokeWidth="1.5" strokeLinecap="round" />
      </g>
    )}
  </svg>
);

const HeroVisual = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;
    let raf = 0, tx = 0, ty = 0, cx = 0, cy = 0, sp = 0;
    const onMove = (e: MouseEvent) => {
      tx = (e.clientX / window.innerWidth - 0.5) * 2;
      ty = (e.clientY / window.innerHeight - 0.5) * 2;
    };
    const tick = () => {
      cx += (tx - cx) * 0.06; cy += (ty - cy) * 0.06;
      // Scroll: custom card slides left, grows, straightens and covers SaaS.
      const target = Math.min(Math.max(window.scrollY / 260, 0), 1);
      sp += (target - sp) * 0.12;
      const k = 1 - sp;
      el.style.setProperty("--rx", `${-cy * 7 * k}deg`);
      el.style.setProperty("--ry", `${(cx * 10 - 16) * k}deg`);
      el.style.setProperty("--p", sp.toFixed(4));
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("mousemove", onMove); };
  }, []);

  const saasPoints = t("hero.visual.saasPoints", { returnObjects: true }) as string[];
  const customPoints = t("hero.visual.customPoints", { returnObjects: true }) as string[];

  return (
    <div aria-hidden="true" className="pointer-events-none select-none" style={{ perspective: "1400px" }}>
      <div
        ref={ref}
        className="relative w-full aspect-[6/5] hero-float"
        style={{ transformStyle: "preserve-3d", transform: "rotateX(var(--rx,0deg)) rotateY(var(--ry,-16deg))" }}
      >
        {/* SaaS */}
        <div className="absolute left-0 top-0 w-[60%] rounded-2xl glass-panel bg-card/70 p-4" style={{ transform: "translateZ(-40px)" }}>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-foreground/70">{t("hero.visual.saasTitle")}</p>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex -space-x-1.5">
              {[0, 1, 2, 3].map((i) => <span key={i} className="w-5 h-5 rounded-full bg-muted border border-border" />)}
            </div>
            <span className="flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"><Users size={10} />× €</span>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className={`relative h-8 rounded-md border border-border bg-muted/40 ${i > 1 ? "opacity-60" : ""}`}>
                {i > 1 && <Lock size={9} className="absolute right-1 top-1 text-muted-foreground" />}
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-md border border-border/60 px-2 py-1.5"><Flow ok={false} /></div>
          <ul className="mt-4 space-y-2 pr-[48%]">
            {saasPoints.map((p) => (
              <li key={p} className="flex items-start gap-2 text-xs text-foreground/85 leading-snug">
                <X size={13} className="mt-px shrink-0 text-destructive" />{p}
              </li>
            ))}
          </ul>
        </div>

        {/* Custom */}
        <div className="absolute right-0 bottom-0 w-[60%] rounded-2xl glass-panel hero-edge p-4 shadow-[0_0_60px_-10px_hsl(var(--accent)/0.35)]" style={{ transformOrigin: "100% 100%", transform: "translateX(calc(var(--p,0) * -40%)) translateY(calc(var(--p,0) * -12%)) translateZ(calc(70px + var(--p,0) * 60px)) scale(calc(1 + var(--p,0) * 0.25))" }}>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent">{t("hero.visual.customTitle")}</p>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex -space-x-1.5">
              {[0, 1, 2, 3].map((i) => <span key={i} className="w-5 h-5 rounded-full bg-accent/20 border border-accent/40" />)}
            </div>
            <span className="flex items-center gap-1 rounded-md bg-accent/10 px-1.5 py-0.5 font-mono text-[10px] text-accent"><Users size={10} /><Minus size={10} /></span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-1.5">
            {[0, 1].map((k) => (
              <div key={k} className="h-[4.5rem] rounded-md border border-accent/30 bg-accent/[0.06] p-1.5 flex items-end gap-0.5">
                {[50, 75, 60, 90, 70, 100].map((h, j) => (
                  <span key={j} className="flex-1 rounded-t-sm bg-gradient-to-t from-primary/50 to-accent hero-col" style={{ height: `${h}%`, animationDelay: `${(k * 6 + j) * 0.12}s` }} />
                ))}
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-md border border-accent/30 px-2 py-1.5"><Flow ok /></div>
          <ul className="mt-4 space-y-2">
            {customPoints.map((p) => (
              <li key={p} className="flex items-start gap-2 text-xs text-foreground leading-snug">
                <Check size={13} className="mt-px shrink-0 text-accent" />{p}
              </li>
            ))}
          </ul>
        </div>

        {/* VS badge */}
        <span className="absolute left-[40%] top-[30%] z-10 -translate-x-1/2 -translate-y-1/2 flex w-9 h-9 items-center justify-center rounded-full glass-panel font-mono text-[10px] font-bold text-accent" style={{ opacity: "calc(1 - var(--p,0) * 1.6)", transform: "translate(-50%,-50%) translateZ(110px)" }}>
          VS
        </span>
      </div>
    </div>
  );
};

export default HeroVisual;
